import React, { useMemo } from 'react';

// Helpers
const getWeekNumber = (d) => {
  const date = new Date(d.valueOf());
  date.setHours(0,0,0,0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const w1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - w1.getTime()) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
};

const getMonday = (d) => {
  const r = new Date(d);
  r.setHours(0,0,0,0);
  const day = r.getDay();
  const diff = r.getDate() - day + (day === 0 ? -6 : 1);
  r.setDate(diff);
  return r;
};

// Afdeling kleur mapping — extensible
const AFDELING_COLORS = {
  10: { bg: '#3b82f6', light: '#dbeafe', name: 'S&T' },
  11: { bg: '#10b981', light: '#d1fae5', name: 'Wimo' },
  20: { bg: '#8b5cf6', light: '#ede9fe', name: 'Rob/Yuri' },
};
const DEFAULT_COLOR = { bg: '#6b7280', light: '#f3f4f6', name: 'Onbekend' };

const monthNames = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];

const WEEK_WIDTH = 32;
const LABEL_WIDTH = 260;
const ROW_HEIGHT = 42;
const N_WEEKS = 52;

const ProjectRoadmap = ({ projecten, onProjectClick }) => {
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const startDate = useMemo(() => getMonday(today), [today]);

  const weeks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < N_WEEKS; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i * 7);
      arr.push({ start: d, weekNum: getWeekNumber(d), month: d.getMonth(), year: d.getFullYear() });
    }
    return arr;
  }, [startDate]);

  const monthSpans = useMemo(() => {
    const spans = [];
    weeks.forEach(w => {
      const key = w.year + '-' + w.month;
      const last = spans[spans.length - 1];
      if (last && last.key === key) last.count++;
      else spans.push({ key, year: w.year, month: w.month, count: 1 });
    });
    return spans;
  }, [weeks]);

  const todayIdx = Math.floor((today - startDate) / (7 * 86400000));

  const calcBar = (project) => {
    const start = project.start_datum ? new Date(project.start_datum) : null;
    const end = project.eind_datum ? new Date(project.eind_datum) : null;
    if (!start && !end) return null;
    const s = start || end;
    const e = end || start;
    const msPerWeek = 7 * 86400000;
    const startIdx = Math.floor((s - startDate) / msPerWeek);
    const endIdx = Math.floor((e - startDate) / msPerWeek);
    if (endIdx < 0 || startIdx >= N_WEEKS) return null;
    const leftIdx = Math.max(0, startIdx);
    const rightIdx = Math.min(N_WEEKS - 1, endIdx);
    return { leftIdx, rightIdx, span: rightIdx - leftIdx + 1 };
  };

  const sortedProjects = useMemo(() => {
    return [...projecten].sort((a, b) => {
      const aS = a.start_datum ? new Date(a.start_datum).getTime() : Infinity;
      const bS = b.start_datum ? new Date(b.start_datum).getTime() : Infinity;
      return aS - bS;
    });
  }, [projecten]);

  const undatedProjects = sortedProjects.filter(p => !calcBar(p));

  return (
    <div style={{ background: 'white', borderRadius: 8, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

      {/* Legenda */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: 11, flexWrap: 'wrap', alignItems: 'center', color: '#374151' }}>
        <strong style={{ fontSize: 12 }}>Afdeling:</strong>
        {Object.values(AFDELING_COLORS).map(c => (
          <span key={c.name}><span style={{ display: 'inline-block', width: 12, height: 12, background: c.bg, verticalAlign: 'middle', marginRight: 4, borderRadius: 2 }}/>{c.name}</span>
        ))}
        <span style={{ marginLeft: 8 }}><strong style={{ fontSize: 12 }}>Status:</strong></span>
        <span>📋 Nieuw (gestreept)</span>
        <span>🚧 Actief (vol)</span>
        <span>✅ Afgerond (gedimd)</span>
        <span style={{ marginLeft: 8 }}><span style={{ display: 'inline-block', width: 2, height: 14, background: '#ef4444', verticalAlign: 'middle', marginRight: 4 }}/>Vandaag</span>
      </div>

      <div style={{ overflowX: 'auto', overflowY: 'visible', border: '1px solid #e5e7eb', borderRadius: 6 }}>
        <div style={{ position: 'relative', width: LABEL_WIDTH + N_WEEKS * WEEK_WIDTH, minWidth: '100%' }}>

          {/* Maand-headers */}
          <div style={{ display: 'flex', height: 26, background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ width: LABEL_WIDTH, padding: '5px 8px', fontSize: 11, fontWeight: 700, color: '#374151', borderRight: '2px solid #d1d5db', background: '#f3f4f6' }}>Project</div>
            {monthSpans.map((m, idx) => (
              <div key={idx} style={{ width: m.count * WEEK_WIDTH, padding: '5px 6px', fontSize: 11, fontWeight: 600, color: '#374151', borderRight: '1px solid #d1d5db', overflow: 'hidden' }}>
                {monthNames[m.month]} {m.year !== today.getFullYear() ? "'" + String(m.year).slice(-2) : ''}
              </div>
            ))}
          </div>

          {/* Week-nummers */}
          <div style={{ display: 'flex', height: 22, background: '#fafbfc', borderBottom: '2px solid #d1d5db' }}>
            <div style={{ width: LABEL_WIDTH, borderRight: '2px solid #d1d5db', background: '#f3f4f6' }}/>
            {weeks.map((w, idx) => (
              <div key={idx} style={{ width: WEEK_WIDTH, textAlign: 'center', fontSize: 9, color: idx === todayIdx ? '#dc2626' : '#9ca3af', borderRight: '1px solid #f3f4f6', padding: '5px 0', fontWeight: idx === todayIdx ? 700 : 400 }}>
                w{w.weekNum}
              </div>
            ))}
          </div>

          {/* Today vertical line */}
          {todayIdx >= 0 && todayIdx < N_WEEKS && (
            <div style={{ position: 'absolute', left: LABEL_WIDTH + todayIdx * WEEK_WIDTH + WEEK_WIDTH/2 - 1, top: 48, height: sortedProjects.filter(p => calcBar(p)).length * ROW_HEIGHT, width: 2, background: '#ef4444', zIndex: 5, pointerEvents: 'none', opacity: 0.7 }} />
          )}

          {/* Project rijen (alleen met datums) */}
          {sortedProjects.filter(p => calcBar(p)).map(p => {
            const bar = calcBar(p);
            const kleur = AFDELING_COLORS[p.afdeling_id] || DEFAULT_COLOR;
            const status = (p.status || 'nieuw').toLowerCase();
            const statusEmoji = status === 'afgerond' ? '✅' : status === 'actief' ? '🚧' : '📋';
            const isStriped = status === 'nieuw';
            const isDimmed = status === 'afgerond';

            return (
              <div key={p.id} style={{ display: 'flex', height: ROW_HEIGHT, borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ width: LABEL_WIDTH, padding: '5px 8px', borderRight: '2px solid #d1d5db', overflow: 'hidden', background: '#fafbfc' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>{p.project_code}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#111827' }}>{p.naam}</div>
                </div>
                <div style={{ display: 'flex', position: 'relative' }}>
                  {weeks.map((w, idx) => (
                    <div key={idx} style={{ width: WEEK_WIDTH, height: ROW_HEIGHT, borderRight: '1px solid #f3f4f6', background: idx === todayIdx ? '#fef3c722' : 'transparent' }} />
                  ))}
                  {bar && (
                    <div
                      onClick={() => onProjectClick && onProjectClick(p.id)}
                      title={p.naam + ' (' + (p.start_datum || '?') + ' → ' + (p.eind_datum || '?') + ') · ' + (p.afdeling_naam || kleur.name) + ' · ' + status}
                      style={{
                        position: 'absolute',
                        left: bar.leftIdx * WEEK_WIDTH + 3,
                        top: 6,
                        width: bar.span * WEEK_WIDTH - 6,
                        height: ROW_HEIGHT - 12,
                        background: isStriped
                          ? 'repeating-linear-gradient(45deg, ' + kleur.bg + ', ' + kleur.bg + ' 5px, ' + kleur.light + ' 5px, ' + kleur.light + ' 10px)'
                          : kleur.bg,
                        opacity: isDimmed ? 0.4 : 1,
                        borderRadius: 4,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 6px',
                        color: 'white',
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                        transition: 'transform 0.1s, box-shadow 0.1s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.25)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.15)'; }}
                    >
                      <span style={{ marginRight: 4, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{statusEmoji}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{p.naam}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Lege state */}
          {sortedProjects.filter(p => calcBar(p)).length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              Geen projecten met start/eind datum in de komende 52 weken.
            </div>
          )}
        </div>
      </div>

      {/* Projecten zonder datums */}
      {undatedProjects.length > 0 && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12 }}>
          <strong style={{ color: '#92400e' }}>📅 Zonder start/eind datum ({undatedProjects.length}):</strong>{' '}
          {undatedProjects.map((p, i) => (
            <span key={p.id}>
              <span onClick={() => onProjectClick && onProjectClick(p.id)} style={{ cursor: 'pointer', color: '#1e40af', textDecoration: 'underline', marginRight: 4 }}>
                {p.naam}
              </span>
              {i < undatedProjects.length - 1 && <span style={{ color: '#9ca3af' }}>· </span>}
            </span>
          ))}
          <div style={{ marginTop: 4, color: '#78350f', fontSize: 11 }}>Tip: voeg start/eind datum toe via bewerken om in de roadmap te verschijnen.</div>
        </div>
      )}
    </div>
  );
};

export default ProjectRoadmap;
